import {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
} from "react";

import { CLASS_CONSTANTS } from "@/utils/constants";

import clsx from "clsx";
import { HtmlOrSvgIcon } from "../icons";
import { scrollToRef } from "@/utils/scrollToRef";
import { css } from "@linaria/core";
import { FlexColumn } from "../Flex";
import { RenderHtml } from "../RenderHtml";

const { ACCORDION } = CLASS_CONSTANTS;

interface IAccordionContext {
  activeTab: string | number | null;
  setActiveAccordion: (activeTab: string | number | null) => void;
  autoUpdate?: boolean;
  connectedAccordion?: boolean;
}

export interface IAccordionProps {
  className?: string;
  autoUpdate?: boolean;
  defaultTab?: string | number | null;
  connectedAccordion: boolean;
}

export interface IAccordionItemProps {
  title: ReactNode;
  className?: string;
  hasError?: boolean;
  scrollOnClick?: boolean;
  itemIndex?: number;
  defaultCollapsed?: boolean;
  header_classname?: string;
  title_classname?: string;
  body_classname?: string;
  renderHeader?: ReactNode;
}

const AccordionContext = createContext<IAccordionContext>({
  activeTab: 0,
  setActiveAccordion: () => {},
  connectedAccordion: true,
});

export const AccordionItem = (
  props: PropsWithChildren<IAccordionItemProps>,
) => {
  const ItemComponent = FlexColumn;
  const TitleComponent = "h4";
  const BodyComponent = "div";
  const { setActiveAccordion, activeTab, connectedAccordion } =
    useContext(AccordionContext);

  const {
    title,
    className = "",
    // onFocus = null,
    hasError = false,
    scrollOnClick = true,
    itemIndex,
    renderHeader,
  } = props;
  const itemHeader = useRef(null);
  const defaultStateRef = useRef(props.defaultCollapsed);
  const tabToActivateOnClick = activeTab !== itemIndex ? itemIndex : null;
  const activeState = !connectedAccordion || activeTab === itemIndex;
  const defaultActiveState =
    connectedAccordion || defaultStateRef.current === null
      ? activeState
      : !defaultStateRef.current;
  const [isActive, setIsActive] = useState(defaultActiveState);

  const classnames = {
    header: clsx(ACCORDION.HEADER, props.header_classname),
    title: clsx(ACCORDION.TITLE, props.title_classname),
    body: clsx(ACCORDION.BODY, props.body_classname),
  };

  useEffect(() => setIsActive(defaultActiveState), [defaultActiveState]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      setIsActive(!isActive);

      if (!connectedAccordion) return;
      setActiveAccordion(tabToActivateOnClick ?? null);
      if (scrollOnClick && tabToActivateOnClick && activeTab) {
        scrollToRef(itemHeader);
      }

      // onFocus && onFocus();
    },
    [
      activeTab,
      connectedAccordion,
      isActive,
      scrollOnClick,
      setActiveAccordion,
      tabToActivateOnClick,
    ],
  );

  return (
    <ItemComponent
      className={clsx(
        className,
        ACCORDION.PANE,
        {
          [ACCORDION.OPEN]: isActive,
          [ACCORDION.CLOSED]: !isActive,
          [ACCORDION.INVALID]: hasError,
          [ACCORDION.VALID]: !hasError,
        },
        accordionItemClassName,
      )}
    >
      <TitleComponent
        ref={itemHeader}
        className={classnames.header}
        onClick={handleClick}
      >
        <span className={classnames.title}>
          {renderHeader || <RenderHtml>{title}</RenderHtml>}
        </span>
        <span className={ACCORDION.ICONS}>
          <span className={ACCORDION.STATE}>
            <HtmlOrSvgIcon type="error" />
          </span>
          <span className={ACCORDION.ARROW}>
            <HtmlOrSvgIcon type="arrow" />
          </span>
        </span>
      </TitleComponent>
      <BodyComponent className={classnames.body}>
        {props.children}
      </BodyComponent>
    </ItemComponent>
  );
};

export const Accordion = (props: PropsWithChildren<IAccordionProps>) => {
  const [activeTab, setActiveAccordion] = useState(props.defaultTab ?? null);

  const { autoUpdate, defaultTab } = props;
  useEffect(() => {
    if (autoUpdate !== true) {
      return;
    }

    setActiveAccordion(defaultTab ?? null);
  }, [autoUpdate, defaultTab]);

  const context = useMemo(
    () => ({
      activeTab,
      setActiveAccordion,
      connectedAccordion: !!props.connectedAccordion,
    }),
    [activeTab, props.connectedAccordion],
  );

  return (
    <AccordionContext.Provider value={context}>
      <div className={clsx(ACCORDION.ROOT, props.className)}>
        {props.children}
      </div>
    </AccordionContext.Provider>
  );
};

const accordionItemClassName = css`
  gap: 0;
  --ws-icon-width: 20px;
  --ws-icon-margin: 10px;
  &:not(:first-child) {
    margin-top: 1rem;
  }

  &:last-of-type {
    margin-bottom: 1rem;
  }

  .${ACCORDION.HEADER} {
    margin: 0 0 10px;
    cursor: pointer;
    position: relative;
    display: flex;
    width: 100%;

    .${ACCORDION.TITLE} {
      flex: 1;
    }
    .${ACCORDION.ICONS} {
      height: 1rem;
      align-self: center;
      display: flex;
      > * {
        width: 1em;
        max-width: 1em;
        flex: 1 0 var(--ws-icon-width);
        width: var(--ws-icon-width);
        max-width: var(--ws-icon-width);
        :not(:last-child) {
          margin-right: 1em;
          margin-right: var(--ws-icon-margin);
        }
      }
    }

    .${ACCORDION.ARROW} {
      transition: 0.3s transform;
      .${ACCORDION.OPEN}& {
        transform: rotate(180deg);
      }
    }

    .${ACCORDION.STATE} {
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      display: none;
      .${ACCORDION.INVALID}& {
        display: block;
      }
    }
    ${CLASS_CONSTANTS.SEMANTIC.SVG} {
      max-height: 100%;
      max-width: 100%;
    }
  }

  &.${ACCORDION.CLOSED} .${ACCORDION.BODY} {
    display: none;
  }
`;
